import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Inbox, Image, MessageSquareQuote, FolderKanban, FileText, LogOut, ExternalLink, Sparkles, HardHat, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/applications", label: "Applications", icon: HardHat },
  { to: "/admin/jobs", label: "Job Postings", icon: Briefcase },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban },
  { to: "/admin/gallery", label: "Gallery", icon: Image },
  { to: "/admin/visualizer", label: "AI Visualizer", icon: Sparkles },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { to: "/admin/content", label: "SEO / Content", icon: FileText },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden md:flex flex-col w-64 bg-secondary text-white shrink-0">
        <Link to="/" className="flex items-center gap-3 h-20 px-6 border-b border-white/10">
          <img src="/archive/logo.jpg" alt="CG" className="h-10 w-10 rounded-sm object-contain bg-white p-0.5" />
          <div className="font-heading font-extrabold">CG ADMIN</div>
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm font-semibold rounded-sm transition-colors ${isActive ? "bg-primary text-white" : "text-white/70 hover:bg-white/10"}`} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/60 hover:text-white"><ExternalLink className="h-4 w-4" /> View site</a>
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white/70 hover:text-white" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-20 bg-card border-b border-border flex items-center justify-between px-6 md:px-8">
          <div className="md:hidden font-heading font-extrabold">CG ADMIN</div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name} · {user?.email}</span>
            <Button onClick={doLogout} variant="outline" size="sm" className="rounded-sm md:hidden"><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        {/* mobile nav */}
        <div className="md:hidden flex overflow-x-auto hide-scrollbar border-b border-border bg-card">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap ${isActive ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </div>
        <main className="flex-1 p-6 md:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
